import React, { useState, useEffect, useRef } from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import '../components/InlineSpeechRecognition.css'

/**
 * Inline Speech Recognition component that directly populates the input field
 *
 * @param {Object} props
 * @param {string} props.targetLanguage - Language code for recognition
 * @param {boolean} props.useGoogleApi - Whether to use Google API instead of Web Speech API
 * @param {Function} props.onTranscriptChange - Callback to update the input field
 * @param {string} props.inputValue - Current value of the input field
 * @param {boolean} props.autoSend - Whether to automatically send transcript when done
 * @param {Function} props.onSend - Function to call when auto-sending
 * @param {string} props.sessionId - Current session ID for Google API
 * @param {Function} props.onListeningChange - Callback when listening state changes
 */
const InlineSpeechRecognition = ({
  targetLanguage,
  useGoogleApi = false,
  onTranscriptChange,
  inputValue = '',
  autoSend = false,
  onSend,
  sessionId,
  onListeningChange
}) => {
  // State
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // References
  const recognitionRef = useRef(null);
  const googleStreamRef = useRef(null);
  const timeoutRef = useRef(null);

  // Notify parent component when listening state changes
  useEffect(() => {
    if (onListeningChange) {
      onListeningChange(isListening);
    }
  }, [isListening, onListeningChange]);

  // Language code mapping
  const getLanguageCode = (language) => {
    const languageMap = {
      'english': 'en-US',
      'french': 'fr-FR',
      'spanish': 'es-ES',
      'german': 'de-DE',
      'italian': 'it-IT',
      'portuguese': 'pt-PT',
      'romanian': 'ro-RO',
      'japanese': 'ja-JP',
      'en': 'en-US',
      'fr': 'fr-FR',
      'es': 'es-ES',
      'de': 'de-DE',
      'it': 'it-IT',
      'ro': 'ro-RO',
      'pt': 'pt-PT',
      'ja': 'ja-JP'
    };

    return languageMap[language?.toLowerCase()] || 'en-US';
  };

  // Clean up function
  useEffect(() => {
    return () => {
      stopListening();
    };
  }, []);

  // Start Web Speech API recognition
  const startWebSpeechRecognition = () => {
    try {
      // Check browser support
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        setError('Speech recognition is not supported in this browser');
        return false;
      }

      // Initialize recognition
      const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognitionAPI();

      // Configure recognition
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = getLanguageCode(targetLanguage);

      // Set up event handlers
      recognitionRef.current.onstart = () => {
        setIsListening(true);
        setIsProcessing(false);
        setError('');
      };

      recognitionRef.current.onresult = (event) => {
        let interim = '';
        let final = '';

        // Process results
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result[0].transcript;

          if (result.isFinal) {
            final += text + ' ';
          } else {
            interim = text;
          }
        }

        // Update input field
        onTranscriptChange(inputValue + final + interim);

        // Reset auto-stop timer
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Auto-stop after 2 seconds of silence
        timeoutRef.current = setTimeout(() => {
          if (recognitionRef.current) {
            stopListening();

            // Auto-send if enabled and there's text
            if (autoSend && (inputValue + final).trim() !== '') {
              onSend();
            }
          }
        }, 2000);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setError(`Error: ${event.error}`);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };

      // Start listening
      recognitionRef.current.start();
      return true;
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setError(`Failed to start speech recognition: ${error.message}`);
      return false;
    }
  };

  // Start Google Speech-to-Text recognition
  const startGoogleRecognition = async () => {
    try {
      setIsProcessing(true);

      // Step 1: Request streaming session from backend
      const response = await apiClient.audio.startSpeechRecognition({
        sessionId: sessionId,
        language: targetLanguage
      });

      const { stream_url: streamUrl, session_id: returnedSessionId } = response.data;

      if (!streamUrl) {
        setError('No stream URL returned from server');
        setIsProcessing(false);
        return false;
      }

      // Step 2: Get mic permission and create audio context
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      googleStreamRef.current = {
        stream,
        audioContext,
        streamUrl,
        sessionId: returnedSessionId,
        websocket: null
      };

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      source.connect(processor);
      processor.connect(audioContext.destination);

      // Step 3: Connect to WebSocket
      const websocket = new WebSocket(streamUrl);
      googleStreamRef.current.websocket = websocket;

      websocket.onopen = () => {
        setIsListening(true);
        setIsProcessing(false);
      };

      websocket.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.transcript) {
          if (data.is_final) {
            onTranscriptChange(inputValue + data.transcript + ' ');
          } else {
            onTranscriptChange(inputValue + data.transcript);
          }

          // Reset silence timer
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            stopListening();

            // Auto-send if enabled and there's text
            if (autoSend && (inputValue + data.transcript).trim() !== '') {
              onSend();
            }
          }, 2000);
        }
      };

      websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('Error connecting to speech recognition service');
        setIsListening(false);
      };

      websocket.onclose = (event) => {
        setIsListening(false);
        setIsProcessing(false);
        if (processor && source) {
          processor.disconnect();
          source.disconnect();
        }
      };

      // Step 4: Stream audio as 16-bit PCM
      processor.onaudioprocess = (e) => {
        if (websocket.readyState === WebSocket.OPEN) {
          const float32 = e.inputBuffer.getChannelData(0);
          const pcm = floatTo16BitPCM(float32);
          websocket.send(pcm);
        }
      };

      function floatTo16BitPCM(float32Array) {
        const buffer = new ArrayBuffer(float32Array.length * 2);
        const view = new DataView(buffer);
        for (let i = 0; i < float32Array.length; i++) {
          let s = Math.max(-1, Math.min(1, float32Array[i]));
          view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        }
        return buffer;
      }

      return true;
    } catch (error) {
      console.error('Error starting Google recognition:', error);
      setError(`Failed to start speech recognition: ${error.message}`);
      setIsProcessing(false);
      setIsListening(false);
      return false;
    }
  };

  // Toggle listening state
  const toggleListening = async () => {
    if (isListening) {
      stopListening();
    } else {
      let success = false;
      if (useGoogleApi) {
        success = await startGoogleRecognition();
      } else {
        success = startWebSpeechRecognition();
      }
    }
  };

  // Stop listening
  const stopListening = () => {
    // Stop Web Speech API
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    // Stop Google Speech-to-Text
    if (googleStreamRef.current) {
      if (googleStreamRef.current.websocket) {
        googleStreamRef.current.websocket.close();
      }

      if (googleStreamRef.current.stream) {
        googleStreamRef.current.stream.getTracks().forEach(track => track.stop());
      }

      if (googleStreamRef.current.audioContext) {
        googleStreamRef.current.audioContext.close();
      }

      googleStreamRef.current = null;
    }

    // Clear any pending timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsListening(false);
    setIsProcessing(false);
  };

  return (
    <Tooltip title={isListening ? "Stop listening" : "Start voice input"}>
      <IconButton
        color={isListening ? "error" : "secondary"}
        onClick={toggleListening}
        disabled={isProcessing}
        aria-label={isListening ? "stop voice input" : "start voice input"}
        sx={{
          backgroundColor: isListening ? 'rgba(244, 67, 54, 0.1)' : 'transparent',
          animation: isListening ? 'pulse 1.5s infinite' : 'none',
          '&:hover': {
            backgroundColor: isListening ? 'rgba(244, 67, 54, 0.2)' : 'rgba(156, 39, 176, 0.1)',
          },
          '&::after': isListening ? {
            content: '""',
            position: 'absolute',
            top: -4,
            left: -4,
            right: -4,
            bottom: -4,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'error.main',
            animation: 'pulseBorder 2s infinite'
          } : {}
        }}
      >
        {isProcessing ? (
          <CircularProgress size={24} color="inherit" />
        ) : isListening ? (
          <MicOffIcon />
        ) : (
          <MicIcon />
        )}
      </IconButton>
    </Tooltip>
  );
};

export default InlineSpeechRecognition;